import { useState, useCallback, useEffect } from "react";
import { useDebounce } from "./useDebounce";

export interface UsePaginationProps {
  initialPage?: number;
  initialLimit?: number;
  initialSearch?: string;
  searchDebounceDelay?: number;
}

/**
 * Hook to manage pagination state.
 */
export function usePagination({
  initialPage = 1,
  initialLimit = 10,
  initialSearch = "",
  searchDebounceDelay = 500,
}: UsePaginationProps = {}) {
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [search, setSearch] = useState(initialSearch);
  const debouncedSearch = useDebounce(search, searchDebounceDelay);

  const nextPage = useCallback(() => setPage((p) => p + 1), []);
  const prevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const resetPage = useCallback(() => setPage(initialPage), [initialPage]);

  // Reset pagination to first page when search changes
  useEffect(() => {
    resetPage();
  }, [debouncedSearch, resetPage]);

  return {
    page,
    setPage,
    limit,
    setLimit,
    nextPage,
    prevPage,
    resetPage,
    search,
    setSearch,
    debouncedSearch,
  };
}
